import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, FilePlus2, FlaskConical, LayoutTemplate, Send, Smartphone, Sparkles, WandSparkles } from "lucide-react";
import { Sheet } from "@/ui";
import { NaraAvatar } from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { naraSuggestions } from "@/mock";

/**
 * Ask Nara — the universal create+ask palette (⌘K). Cross-cutting: lives in the
 * shell so any screen can summon Nara. Submitting hands the prompt to Make with
 * the text prefilled, entering the see→make→measure loop.
 */
interface AskNaraState {
  open: boolean;
  setOpen: (o: boolean) => void;
}
const Ctx = createContext<AskNaraState | null>(null);

export function AskNaraProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <Ctx.Provider value={{ open, setOpen }}>
      {children}
      <AskNaraPalette open={open} onClose={() => setOpen(false)} />
    </Ctx.Provider>
  );
}

export function useAskNara(): AskNaraState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAskNara must be used inside <AskNaraProvider>");
  return ctx;
}

/** Quick-starts. `newPage` opens Make on a freshly-created blank page (the orb's
 *  "start something new" intent); `to` routes elsewhere (e.g. the Library). */
const QUICK: {
  icon: typeof WandSparkles;
  label: string;
  prompt?: string;
  newPage?: boolean;
  to?: string;
}[] = [
  { icon: WandSparkles, label: "Make a landing page", prompt: "Make a new landing page.", newPage: true },
  { icon: FlaskConical, label: "Start an A/B test", prompt: "Start an A/B test of the homepage hero." },
  { icon: LayoutTemplate, label: "Start from a template", to: "/library" },
];

function AskNaraPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const ws = useWorkspace();
  const [text, setText] = useState("");

  // Connect mobile analytics: switch the workspace to a mobile (app) property —
  // which flips the whole shell into app mode — and jump straight to the SDK page.
  const connectMobile = useCallback(() => {
    onClose();
    setText("");
    const appProp = ws.properties.find((p) => p.propertyKind === "app");
    if (appProp) ws.setCurrent(appProp.id);
    nav("/sdk");
  }, [ws, nav, onClose]);

  const go = useCallback(
    (prompt: string, opts?: { newPage?: boolean }) => {
      onClose();
      setText("");
      nav("/make", { state: { prompt, newPage: opts?.newPage ?? false } });
    },
    [nav, onClose],
  );

  // Open Make on a brand-new blank page (no prompt) — the editor's "new project".
  const startBlank = useCallback(() => {
    onClose();
    setText("");
    nav("/make", { state: { newPage: true } });
  }, [nav, onClose]);

  const runQuick = useCallback(
    (q: (typeof QUICK)[number]) => {
      if (q.to) {
        onClose();
        setText("");
        nav(q.to);
        return;
      }
      go(q.prompt ?? "", { newPage: q.newPage });
    },
    [go, nav, onClose],
  );

  return (
    <Sheet open={open} onClose={onClose}>
      <div className="flex items-center gap-3">
        <NaraAvatar />
        <div>
          <p className="text-[15px] font-semibold tracking-tight text-ink-strong">Ask Nara</p>
          <p className="text-[13px] text-muted">Tell her what to make — or pick a quick start.</p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) go(text.trim(), { newPage: true });
        }}
        className="mt-4"
      >
        <div className="flex items-center gap-2 rounded-btn border border-hairline bg-paper-2 px-3 focus-within:border-accent dark:bg-white/5">
          <Sparkles className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.5} />
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Make a waitlist page for the iOS app…"
            className="h-11 w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-subtle"
          />
          <button
            type="submit"
            aria-label="Send to Nara"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-white hover:bg-accent-hover"
          >
            <Send className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </form>

      <div className="mt-4 space-y-1">
        {/* The signature "new project" entry: open the builder on a fresh blank page. */}
        <button
          onClick={startBlank}
          className="flex w-full items-center gap-3 rounded-btn border border-hairline bg-paper-2/60 px-3 py-2.5 text-left text-[14px] font-medium text-ink-strong transition-colors hover:border-accent/40 hover:bg-accent-soft dark:bg-white/5"
        >
          <FilePlus2 className="h-[18px] w-[18px] text-accent" strokeWidth={1.75} />
          Start a blank page
          <ArrowUpRight className="ml-auto h-4 w-4 text-subtle" strokeWidth={1.5} />
        </button>
        <button
          onClick={connectMobile}
          className="flex w-full items-center gap-3 rounded-btn px-3 py-2.5 text-left text-[14px] text-ink transition-colors hover:bg-paper-2 dark:hover:bg-white/5"
        >
          <Smartphone className="h-[18px] w-[18px] text-muted" strokeWidth={1.5} />
          Connect mobile analytics
          <ArrowUpRight className="ml-auto h-4 w-4 text-subtle" strokeWidth={1.5} />
        </button>
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <button
              key={q.label}
              onClick={() => runQuick(q)}
              className="flex w-full items-center gap-3 rounded-btn px-3 py-2.5 text-left text-[14px] text-ink transition-colors hover:bg-paper-2 dark:hover:bg-white/5"
            >
              <Icon className="h-[18px] w-[18px] text-muted" strokeWidth={1.5} />
              {q.label}
              <ArrowUpRight className="ml-auto h-4 w-4 text-subtle" strokeWidth={1.5} />
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="eyebrow mb-2">Try</p>
        <div className="flex flex-wrap gap-2">
          {naraSuggestions.map((s) => (
            <button
              key={s}
              onClick={() => go(s, { newPage: true })}
              className="rounded-chip bg-paper-2 px-2.5 py-1 text-[12px] text-muted transition-colors hover:text-ink dark:bg-white/5"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
